import { http } from "../lib/http";
import { getUser } from "../lib/authToken";
import { DEV_USER } from "../config/localAuth";

/**
 * Fetch calendar events for a specific month
 * @param {Object} params 
 * @param {number} [params.restaurantId] 
 * @param {string} [params.location] 
 * @param {number} [params.month]
 * @param {number} [params.year] 
 * @returns {Promise<Object>} Calendar data with events and categories
 */
export async function fetchCalendarMonth({
  restaurantId,
  location,
  month,
  year,
} = {}) {
  const user = getUser() || DEV_USER;
  restaurantId = restaurantId || user.restaurant_id;
  location = location || user.location;

  const now = new Date();
  month = month || (now.getMonth() + 1);
  year = year || now.getFullYear();

  console.log("➡️ fetchCalendarMonth called with:", { restaurantId, location, month, year });

  const qs = new URLSearchParams({
    restaurant_id: restaurantId,
    location,
    year: year.toString(),
    month: month.toString(),
  });

  console.log("🔗 Final API URL:", `/fe/calendar/month_schedule?${qs.toString()}`);

  const dto = await http.get(`/fe/calendar/month_schedule?${qs.toString()}`);
  console.log("📦 API Response (dto):", dto);

  return mapCalendarToUI(dto);
}

/**
 * Create a new schedule
 * @param {Object} payload - UI payload from modal
 * @returns {Promise<Object>} Created event
 */
export async function createSchedule(payload) {

  const apiPayload = {
    category: payload.type,
    title: payload.title,
    start: payload.start, // Already formatted as "YYYY-MM-DD HH:mm:ss" in IST
    end: payload.end, // Already formatted as "YYYY-MM-DD HH:mm:ss" in IST
    importance: payload.importance === "important",
    team: payload.assignees,
    links: payload.links,
    notes: payload.notes,
    is_active: true,
    restaurant_id: payload.restaurant_id,
  };

  console.log("➡️ createSchedule API call:", { url: "/fe/calendar/add_schedule", payload: apiPayload });

  const response = await http.post("/fe/calendar/add_schedule", apiPayload);
  console.log("📦 createSchedule API Response:", response);

  return mapEventToUI(response);
}

/**
 * Update an existing schedule
 * @param {number} eventId - The ID of the event to update
 * @param {Object} payload - UI payload from modal
 * @returns {Promise<Object>} Updated event
 */
export async function updateSchedule(eventId, payload) {
  const apiPayload = {
    category: payload.type,
    title: payload.title,
    start: payload.start,
    end: payload.end, 
    importance: payload.importance === "important",
    team: payload.assignees,
    links: payload.links,
    notes: payload.notes,
    is_active: true,
    restaurant_id: payload.restaurant_id,
  };

  console.log("➡️ updateSchedule API call:", { url: `/fe/calendar/modify_schedule/${eventId}`, payload: apiPayload });

  const response = await http.patch(`/fe/calendar/modify_schedule/${eventId}`, apiPayload);
  console.log("📦 updateSchedule API Response:", response);

  // API returns {"message": "Event updated successfully", "event_id": 42}
  // Return the updated event with id from response
  const updatedEvent = { ...payload, id: response.event_id };
  return mapEventToUI(updatedEvent);
}

/**
 * Map single event from API to UI format
 * @param {Object} event - API event
 * @returns {Object} Mapped event
 */
function mapEventToUI(event) {
  return {
    id: event.id,
    title: event.title,
    type: event.category,
    start: event.start,
    end: event.end,
    all_day: event.all_day,
    assignees: event.team || [],
    links: event.links || "",
    notes: event.notes,
    importance: event.importance ? "important" : "normal",
    restaurant_id: event.restaurant_id,
    version: event.version,
    createdAt: event.createdAt,
    is_active: event.is_active,
  };
}

/**
 * Map API response to UI format
 * @param {Object|Array} dto - API response (object with categories/events or array of events)
 * @returns {Object} Mapped calendar data
 */
function mapCalendarToUI(dto) {
  let events = [];
  let categories = {};

  if (Array.isArray(dto)) {
    // API returns array of events directly
    events = dto;
    // Default categories for known types
    categories = {
      "APPOINTMENT": { "bg": "#FFE6CC", "fg": "#2f2b27" },
      "EVENT": { "bg": "#1F1F1F", "fg": "#FFFFFF" },
      "MEETING": { "bg": "#FFE6CC", "fg": "#2f2b27" },
      "PRESENTATION": { "bg": "#FDEBD7", "fg": "#2f2b27" },
    };
  } else {
    // API returns object with categories and events
    categories = dto?.categories || {};
    events = dto?.events || [];
  }

  const mappedEvents = events.map(e => ({
    id: e.id,
    title: e.title,
    type: e.category, // Map category to type for consistency
    start: e.start,
    end: e.end,
    location: e.location,
    participants: e.team || e.participants || [], // Handle 'team' from new API or 'participants' from mock
    attendees_count: e.attendees_count || (e.team ? e.team.length : 0),
    notes: e.notes,
    importance: e.importance || "normal",
    links: e.links,
    version: e.version,
    createdAt: e.createdAt,
    restaurant_id: e.restaurant_id,
  }));

  // Map categories to colors format used in UI
  const colors = {};
  Object.keys(categories).forEach(key => {
    colors[key] = categories[key].bg; // Use bg color
  });

  return {
    timezone: dto?.timezone || "Asia/Kolkata",
    colors,
    events: mappedEvents,
  };
}
